import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'i18n-js';
import I18nContext from 'contexts/i18n/context';
import moment from 'moment';
import Moment from 'react-moment';
import isNil from 'lodash/isNil';
import { DEFAULT_LOCALE, DEVELOPMENT, STAGING } from 'constants';
import { Loader } from 'components/ui';
import sessionStore from 'services/session_store';
import { SHARE } from 'constants';
import { getUrlSearchParams } from 'services/url';

Moment.globalMoment = moment;

const COOKIE_NAME = 'locale';
const i18n = new I18n();

export const setCookieLocale = (locale) => {
  sessionStore.set(COOKIE_NAME, locale);
};

export const getCookieLocale = () => {
  return sessionStore.get(COOKIE_NAME);
};

export const setForcedLocale = (locale) => {
  sessionStore.set('FORCED_LOCALE', locale);
};

export const getForcedLocale = () => {
  return sessionStore.get('FORCED_LOCALE');
};

export const getUrlLocale = () => {
  return getUrlSearchParams('locale');
};

const loadLocaleData = async (locale) => {
  switch (locale) {
    case 'fr':
      return import('locales/translation.fr.yml');
    case 'nl':
      return import('locales/translation.nl.yml');
    case 'en':
    default:
      return import('locales/en.yml');
  }
};

const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(getCookieLocale());
  const [i18nInstance, setI18nInstance] = useState(null);

  const getLocale = () => {
    const cookieLocale = getCookieLocale();
    return isNil(i18nInstance.locale) ? (isNil(cookieLocale) ? DEFAULT_LOCALE : cookieLocale) : i18nInstance.locale;
  };

  const t = (text, params) => {
    return i18nInstance.t(text, { ...params });
  };

  const t_html = (text, params) => {
    const trusted = {
      __html: i18nInstance.t(text, { ...params }),
    };
    /* eslint-disable react/no-danger */
    return <span dangerouslySetInnerHTML={trusted} />;
    /* eslint-enable */
  };

  const initI18nInstance = async () => {
    const cookieLocale = getCookieLocale();

    const localeToInit = isNil(cookieLocale) ? DEFAULT_LOCALE : cookieLocale;

    const localeData = await loadLocaleData(localeToInit);

    i18n.store(localeData.default);
    Moment.globalLocale = localeToInit;
    i18n.defaultLocale = localeToInit;
    i18n.locale = localeToInit;

    setI18nInstance(i18n);
    setCookieLocale(localeToInit);
    if (DEVELOPMENT || STAGING) {
      window.i18n = i18n;
    }
  };

  useEffect(() => {
    initI18nInstance();
  }, []);

  const updateLocale = async (localeToUpdate) => {
    const forcedLocale = getForcedLocale();
    const urlLocale = getUrlLocale();

    // Déterminer quelle locale utiliser selon les priorités
    let targetLocale = localeToUpdate;

    if (SHARE) {
      if (urlLocale) {
        targetLocale = urlLocale;
      } else if (forcedLocale) {
        targetLocale = forcedLocale;
      }
    }

    // Appliquer la locale choisie
    const localeData = await loadLocaleData(targetLocale);
    i18nInstance.store(localeData.default);
    window.locale = targetLocale;
    Moment.globalLocale = targetLocale;
    i18nInstance.locale = targetLocale;
    setCookieLocale(targetLocale);
    setI18nInstance(i18nInstance);
    setLocale(targetLocale);
  };

  if (i18nInstance === null) {
    return <Loader />;
  }

  return (
    <I18nContext.Provider
      key={`current-locale-${locale}`}
      value={{
        getCurrentLocale: getLocale,
        getForcedLocale,
        setForcedLocale,
        getCookieLocale,
        setCookieLocale,
        setLocale,
        setCurrentLocale: (localeToSet) => {
          updateLocale(localeToSet);
        },
        t,
        t_html,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

I18nProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

export default I18nProvider;
